import {
  Accreditation,
  Composition,
} from '@spreadgroup/common-partner-lib/composition';

export type CompositionPosStateKey =
  | 'PUBLISHED'
  | 'PENDING'
  | 'UNPUBLISHED'
  | 'REJECTED';

export type CompositionPosState = {
  key: CompositionPosStateKey;
  name: string;
  description: string;
  badgeType: 'success' | 'warning' | 'neutral' | 'error';
  color: string;
};

export const COMPOSITION_POS_STATES: Record<
  CompositionPosStateKey,
  CompositionPosState
> = {
  PUBLISHED: {
    key: 'PUBLISHED',
    name: 'PUBLISHING.STATUS.PUBLISHED.NAME',
    description: 'PUBLISHING.STATUS.PUBLISHED.DESCRIPTION',
    badgeType: 'success',
    color: '#3C854A',
  },
  PENDING: {
    key: 'PENDING',
    name: 'PUBLISHING.STATUS.PENDING.NAME',
    description: 'PUBLISHING.STATUS.PENDING.DESCRIPTION',
    badgeType: 'warning',
    color: '#BD8F2B',
  },
  UNPUBLISHED: {
    key: 'UNPUBLISHED',
    name: 'PUBLISHING.STATUS.UNPUBLISHED.NAME',
    description: 'PUBLISHING.STATUS.UNPUBLISHED.DESCRIPTION',
    badgeType: 'neutral',
    color: '#A3A5A7',
  },
  REJECTED: {
    key: 'REJECTED',
    name: 'PUBLISHING.STATUS.REJECTED.NAME',
    description: 'PUBLISHING.STATUS.REJECTED.DESCRIPTION',
    badgeType: 'error',
    color: '#9B4636',
  },
};

type CompositionPosTypeKey = 'MP' | 'SHOP';

type CompositionPosSelectionState = boolean;
type CompositionPosPublishingStatus = string;
type CompositionAccreditationStatus = Accreditation['state'] | undefined;

export type CompositionPosType = {
  key: CompositionPosTypeKey;
  icon: string;
  getSelectionState: (composition: Composition) => CompositionPosSelectionState;
  getPublishingStatus: (
    composition: Composition,
  ) => CompositionPosPublishingStatus;
  getAccreditationStatus: (
    composition: Composition,
  ) => CompositionAccreditationStatus;
};

export const COMPOSITION_POS_TYPES: Record<
  CompositionPosTypeKey,
  CompositionPosType
> = {
  MP: {
    key: 'MP',
    icon: 'sprd-heart',
    getSelectionState: (composition: Composition) =>
      composition.publishingDetails.d2c.mp,
    getPublishingStatus: (composition: Composition) =>
      composition.publishingDetails.d2c.publishingStatus,
    getAccreditationStatus: (composition: Composition) =>
      composition.publishingDetails.d2c.mpAccreditation?.state,
  },
  SHOP: {
    key: 'SHOP',
    icon: 'shop',
    getSelectionState: (composition: Composition) =>
      composition.publishingDetails.spreadShops.shops.length > 0,
    getPublishingStatus: (composition: Composition) =>
      getCombinedShopPublishingStatus(composition),
    getAccreditationStatus: (composition: Composition) =>
      composition.publishingDetails.spreadShops.accreditation?.state,
  },
};

// if at least one shop is online we consider shop in general as online
export function getCombinedShopPublishingStatus(composition: Composition) {
  return composition.publishingDetails.spreadShops.shops.some(
    (shop) => shop.publishingStatus === 'ONLINE',
  )
    ? 'ONLINE'
    : 'OFFLINE';
}

export function getCompositionPosStatus({
  publishingStatus,
  accreditationStatus,
  selectionState,
}: {
  publishingStatus: CompositionPosPublishingStatus;
  accreditationStatus: CompositionAccreditationStatus;
  selectionState: CompositionPosSelectionState;
}) {
  if (publishingStatus === 'ONLINE' && selectionState) {
    return COMPOSITION_POS_STATES.PUBLISHED;
  } else if (accreditationStatus === 'REJECTED') {
    return COMPOSITION_POS_STATES.REJECTED;
  } else if (!selectionState) {
    return COMPOSITION_POS_STATES.UNPUBLISHED;
  } else {
    return COMPOSITION_POS_STATES.PENDING;
  }
}

export function getAllCompositionPosStates({
  composition,
  relevantPosTypes = Object.values(COMPOSITION_POS_TYPES),
  relevantPosStates = Object.values(COMPOSITION_POS_STATES),
}: {
  composition: Composition;
  relevantPosTypes: CompositionPosType[];
  relevantPosStates: CompositionPosState[];
}) {
  return relevantPosTypes
    .map((posType) => ({
      posType: posType,
      status: getCompositionPosStatus({
        selectionState: posType.getSelectionState(composition),
        publishingStatus: posType.getPublishingStatus(composition),
        accreditationStatus: posType.getAccreditationStatus(composition),
      }),
    }))
    .filter((posState) =>
      relevantPosStates.some(
        (relevantPosState) => relevantPosState.key === posState.status.key,
      ),
    );
}

export function getAllCompositionPosStatesCombined(options: {
  composition: Composition;
  relevantPosTypes: CompositionPosType[];
  relevantPosStates: CompositionPosState[];
}) {
  const posStates = getAllCompositionPosStates(options);

  const posStatesCombined = {} as Record<
    CompositionPosStateKey,
    {
      posTypes: CompositionPosType[];
      status: CompositionPosState;
    }
  >;

  posStates.forEach((posState) => {
    if (!posStatesCombined[posState.status.key]) {
      posStatesCombined[posState.status.key] = {
        posTypes: [],
        status: posState.status,
      };
    }

    posStatesCombined[posState.status.key].posTypes.push(posState.posType);
  });
  return posStatesCombined;
}

export function getCompositionPosStateForPosType({
  composition,
  posType,
}: {
  composition: Composition;
  posType: CompositionPosType;
}) {
  return {
    posType: posType,
    status: getCompositionPosStatus({
      selectionState: posType.getSelectionState(composition),
      publishingStatus: posType.getPublishingStatus(composition),
      accreditationStatus: posType.getAccreditationStatus(composition),
    }),
  };
}

export function createInitialPosStateCounts(): {
  total: number;
  stateCounts: Record<CompositionPosStateKey, number>;
} {
  const posStateCounts = Object.keys(COMPOSITION_POS_STATES).reduce(
    (acc, key) => {
      acc[key as CompositionPosStateKey] = 0;
      return acc;
    },
    {} as Record<CompositionPosStateKey, number>,
  );
  return {
    total: 0,
    stateCounts: posStateCounts,
  };
}

export function isInstantlyPublished(
  composition: Composition,
  posType: CompositionPosType,
): boolean {
  return (
    posType.getPublishingStatus(composition) === 'ONLINE' &&
    (posType.getAccreditationStatus(composition) === 'PENDING' ||
      posType.getAccreditationStatus(composition) === undefined)
  );
}
